// Libs
import classNames from 'classnames/bind';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Avatar, Box } from '@mui/material';
// Components, Layouts, Pages
// Others
import { StorageEnum } from '~/utils/enum/base';
import { useAppSelector } from '~/utils/redux/hooks';
import {
  getAvatarName,
  getFileExtension,
  getFullName,
} from '~/utils/helper/common';
import { IFileMessage, IMessage } from '~/utils/interface/message';
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { MessageFileTypeEnum, MessageTypeEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './ConversationMessage.module.scss';
import { icons } from '~/assets';

type Props = {
  data: IMessage;
};

const cx = classNames.bind(styles);

const ConversationMessage = (props: Props) => {
  //#region Destructuring Props
  const { data } = props;
  const accountId = localStorage.getItem(StorageEnum.ACCOUNT_ID);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/conversation']);
  const [isMyMessage] = useState<boolean>(() => {
    if (!accountId) {
      return false;
    }
    return data.senderAccountId === accountId;
  });
  //#endregion Declare Hook

  //#region Selector
  const { tempIdSendFile, typeFileLoading } = useAppSelector((state) => state.conversation);
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const renderContentMessage = () => {
    switch (data.type) {
      case MessageTypeEnum.TEXT:
        return (
          <Box className={cx('textMessageContent')}>
            <Box component={'p'}>{data.content}</Box>
          </Box>
        );

      case MessageTypeEnum.FILE:
        const firstFile = data?.files?.[DEFAULT_NUMBER_ZERO];

        return tempIdSendFile === data.id ? (
          <Box className={cx('loadingWrapContainer')}>
            <Box
              className={cx(
                'loadingContainer',
                typeFileLoading && !typeFileLoading.includes('image') && 'loadingFile'
              )}
            >
              <Box className={cx('loadingContent')} />
            </Box>
          </Box>
        ) : (
          renderFileItem(firstFile)
        );
    }
  };

  const renderFileItem = (firstFile: IFileMessage | undefined) => {
    if (!firstFile) return;

    switch (firstFile?.type) {
      case MessageFileTypeEnum.IMAGE:
        return (
          <Link to={firstFile?.url} className={cx('viewImage')} target='_blank'>
            <img src={firstFile?.url} alt={t('common_img_text_alt')} className={cx('photoMessage')} />
          </Link>
        );

      case MessageFileTypeEnum.FILE:
        return (
          <Link to={firstFile?.url} className={cx('viewFile')} target='_blank'>
            <img src={icons.commonIconDocument} alt={t('common_img_text_alt')} className={cx('fileImg')} />
            <Box className={cx(isMyMessage, 'nameFile')}>
              <Box component={'p'} className={cx('file')}>{`${firstFile.name || EMPTY_STRING}`}</Box>
              <Box component={'p'}>{`.${getFileExtension(firstFile?.url)}`}</Box>
            </Box>
          </Link>
        );

      case MessageFileTypeEnum.MEDIA:
        return (
          <Link to={firstFile?.url} className={cx('viewFile')} target='_blank'>
            <img src={icons.commonIconVideo} alt={t('common_img_text_alt')} className={cx('fileImg')} />
          </Link>
        );
    }
  };
  //#endregion Handle Function

  return (
    <Box
      id='conversationMessageComponent'
      className={cx(isMyMessage ? 'messageIsMe' : 'message')}
      data-message-id={data.id.toString()}
    >
      <Box className={cx('conversationMessageContent', { isMyMessage: data.senderAccountId === accountId })}>
        <Box className={cx('avatarFirstLastName')}>
          {data?.senderAvatarImage ? (
            <Avatar
              alt={t('common_alt_img', { ns: 'common/base' })}
              src={data?.senderAvatarImage}
              className={cx('avatar')}
            />
          ) : (
            <Avatar alt={t('common_alt_img', { ns: 'common/base' })} className={cx('avatar')}>
              {getAvatarName(
                getFullName({ firstName: data?.senderFirstName, lastName: data?.senderLastName })
              )}
            </Avatar>
          )}
        </Box>

        {renderContentMessage()}
      </Box>
    </Box>
  );
};

export default ConversationMessage;
